import './Engagements.scss';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { GetEngagemets } from '../../../../slices/marketplace/timeline/thunks/get-timeline-engagements.thunk';
import { GetTimelineStages } from '../../../../slices/marketplace/timeline/thunks/get-timeline-stages.thunk';
import Helpers from '../../../../utils/helper';
import BootstrapTable, {
    TableColumnFilterProps,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory, {
    textFilter,
    dateFilter,
    selectFilter,
    FILTER_TYPES,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import { Button } from 'reactstrap';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { CreateTimelineModal } from '../../Modals/CreateTimelineModal/CreateTimelineModal';
import ReactDOMServer from 'react-dom/server';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import {
    EngagementProcessStatus,
    EngagementProcessStatusOptions,
    EngagementProcessStatuses,
    EngagementProcessTransitions,
} from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import {
    LevelOfServiceOptions,
    LevelOfService,
    LevelsOfService,
} from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { resetCurrentTimeline } from '../../../../slices/marketplace/timeline/timeline.slice';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { ShowFirstThenFullTextOnHoverLabel } from '../../../../components/ShowFirstThenFullTextOnHoverLabel/ShowFirstThenFullTextOnHoverLabel';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import EditableNameDropdown from '../../TimelinePage/EditableNameDropdown/EditableNameDropdown';
import { GetBrokersList } from '../../../../slices/marketplace/broker/thunks/get-brokers-list.thunk';
import { MarketplaceBrokerUserInfo } from '../../../../slices/marketplace/broker/models/broker';
import UserDetailsModal from '../../../Admin/Permissions/Users/Modal/UserDetailsModal';
import { useFeatureFlag } from '../../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../../FeatureFlags/FeatureFlags.enum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { EngagementObserver } from '../../../../slices/marketplace/timeline/models/engagement-observer.model';
import ObserversManagementModal from './ObserversManagementModal/ObserversManagementModal';

const Engagements = () => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();

    const axios = useStateSelector((state) => state.core.axios);
    const engagementsTable = useStateSelector(
        (state) => state.timelines.engagementTimelinesTable
    );
    const brokersList = useStateSelector(
        (state) => state.brokersState.brokersList
    );

    const isSellers28Implemented = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_8
    );

    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'id',
            order: 'asc',
        },
    ];
    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'id',
        })
    );

    const [engagements, setEngagements] = useState<Timeline[]>([]);
    const [selectedEngagement, setSelectedEngagement] = useState<Timeline>();

    const [statusToBeSet, setStatusToBeSet] =
        useState<EngagementProcessStatus>();
    const [isSavingStatus, setIsSavingStatus] = useState(false);

    const [brokerIdToBeSet, setBrokerIdToBeSet] = useState<number>(0);
    const [isSelectingBroker, setIsSelectingBroker] = useState(false);
    const [isSavingBroker, setIsSavingBroker] = useState(false);
    const [selectedBroker, setSelectedBroker] = useState<
        MarketplaceBrokerUserInfo | undefined
    >(undefined);

    const [selectedObserver, setSelectedObserver] = useState<
        MarketplaceBrokerUserInfo | undefined
    >(undefined);

    const [managingObserversForEngagement, setManagingObserversForEngagement] =
        useState<Timeline | undefined>(undefined);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [engagedBuyersOptions, setEngagedBuyersOptions] = useState<
        MarketplaceBuyer[]
    >([]);
    const [engagementToUpdateBrokerStatus, setEngagementToUpdateBrokerStatus] =
        useState<Timeline | undefined>(undefined);
    const [isSavingSuspendStatus, setIsSavingSuspendStatus] = useState(false);

    const fetchEngagements = async (sort?: TableFilter) => {
        await dispatch(GetEngagemets(sort ?? currentSort));
    };

    const fetchStages = async () => {
        await dispatch(GetTimelineStages());
    };

    const fetchBrokersList = async () => {
        await dispatch(GetBrokersList());
    };

    const initiate = async () => {
        initializeEngagedBuyersOptions();
        await fetchStages();
        await fetchBrokersList();
        await fetchEngagements();
        setIsLoading(false);
    };

    useEffect(() => {
        initiate().then();
    }, []);

    useEffect(() => {
        setEngagements(engagementsTable.timelines);
        setPage(engagementsTable.pageNumber);
        setTotalSize(engagementsTable.totalItems);
        setSizePerPage(engagementsTable.pageSize);
    }, [fetchEngagements]);

    const nestedFieldsMap = [
        {
            name: 'currentEvent',
            sortPath: 'currentEvent.stage',
            filterPath: 'currentEvent.stageName',
        },
        {
            name: 'engagementProcess.engagedBuyers',
            sortPath: 'engagementProcess.engagedBuyers.length',
            filterPath: 'engagementProcess.engagedBuyers.companyName',
        },
        {
            name: 'engagementProcess.practices',
            sortPath: 'engagementProcess.practices.practiceName',
            filterPath: 'engagementProcess.practices.practiceName',
            operator: 'eq',
        },
        {
            name: 'engagementProcess.practices',
            sortPath: 'engagementProcess.practices.practiceName',
            filterPath: 'engagementProcess.practices.practiceName',
            operator: 'eq',
        },
        {
            name: 'engagementProcess.broker.brokerId',
            sortPath: 'engagementProcess.broker.brokerName',
            filterPath: 'engagementProcess.broker.brokerName',
        },
        {
            name: 'engagementProcess.observers',
            sortPath: 'engagementProcess.observers.count',
            filterPath: 'engagementProcess.observers.brokerName',
        },
    ];

    const handleTableChange = (
        _type: any,
        newState: {
            page: number;
            sizePerPage: number;
            sortField: string;
            sortOrder: 'asc' | 'desc';
            filters: {
                [key: string]: {
                    filterVal: any;
                    filterType: string;
                    comparator: any;
                };
            };
        }
    ) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const nestedPropPath =
            nestedFieldsMap.find((nf) => nf.name == sortField)?.sortPath ??
            sortField;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: nestedPropPath }
        );

        const arrayOfObj = Object.entries(newState.filters).map((e) => ({
            [e[0]]: e[1],
        }));

        if (arrayOfObj.length > 0) {
            arrayOfObj.forEach((element) => {
                let key = Object.keys(element);
                let values = newState.filters[key.toString()];

                if (
                    values.filterType != FILTER_TYPES.DATE ||
                    values.filterVal.date != null
                ) {
                    const nestedField = nestedFieldsMap.find(
                        (nf) => nf.name == key[0]
                    );
                    const field = nestedField?.filterPath ?? key[0];

                    let filterOperator =
                        values.filterType == FILTER_TYPES.DATE ||
                        values.filterType == FILTER_TYPES.SELECT
                            ? 'eq'
                            : 'contains';

                    if (nestedField?.operator) {
                        filterOperator = nestedField.operator;
                    }

                    let obj = {
                        Field: field,
                        Operator: filterOperator,
                        Value:
                            values.filterType == FILTER_TYPES.DATE
                                ? moment(values.filterVal.date).format(
                                      'MM.DD.YYYY'
                                  )
                                : values.filterVal,
                    };

                    updatedPostObj.filter.filters.push(obj);
                }
            });
        }

        setCurrentSort(updatedPostObj);
        fetchEngagements(updatedPostObj);
    };

    const launchUpdateStatusProcess = (
        status: EngagementProcessStatus,
        engagement: Timeline
    ) => {
        setSelectedEngagement(engagement);
        setStatusToBeSet(status);
    };

    const onBrokerUpdateConfirmation = async () => {
        setIsSavingBroker(true);
        axios
            .put(
                `/api/marketplace/engagements/${selectedEngagement.id}/broker`,
                { id: brokerIdToBeSet > 0 ? brokerIdToBeSet : null },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .then((response: any) => {
                if (response.status === 200) {
                    createNotification(
                        'Engagement Advisor updated successfully',
                        'success'
                    );
                } else {
                    const message = response.response.data.detail;
                    createNotification(message, 'error');
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred  during Advisor update',
                    'error'
                );
            })
            .finally(() => {
                setIsSavingBroker(false);
                setIsSelectingBroker(false);
                setSelectedEngagement(undefined);
                setBrokerIdToBeSet(0);
                fetchEngagements();
                fetchBrokersList();
            });
    };

    const onStatusUpdateConfirmation = async () => {
        setIsSavingStatus(true);
        axios
            .put(
                `/api/marketplace/engagements/${selectedEngagement.id}/status`,
                statusToBeSet,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    createNotification(
                        'Engagement status updated successfully',
                        'success'
                    );
                } else {
                    createNotification(
                        'An unexpected error occurred  during Status update',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred  during Status update',
                    'error'
                );
            })
            .finally(() => {
                fetchEngagements();
                setIsSavingStatus(false);
                setSelectedEngagement(undefined);
                setStatusToBeSet(undefined);
            });
    };

    const initializeEngagedBuyersOptions = () => {
        axios.get('api/Marketplace/MarketplaceBuyers/all').then((response) => {
            if (response.status === 200) {
                const mappedOptions = response.data
                    .sort((a: MarketplaceBuyer, b: MarketplaceBuyer) =>
                        a.userFullName.localeCompare(b.userFullName)
                    )
                    .reduce(
                        (filterOptionsObject: any, buyer: MarketplaceBuyer) => {
                            filterOptionsObject[
                                buyer.corporateAffiliationName
                            ] = buyer.corporateAffiliationName;
                            return filterOptionsObject;
                        },
                        {}
                    ) as MarketplaceBuyer[];

                setEngagedBuyersOptions(mappedOptions);
            }
        });
    };

    const textFilterProps = textFilter({
        placeholder: 'Name',
    }) as TableColumnFilterProps;

    const engagementProcessStatusFilterProps = selectFilter({
        options: EngagementProcessStatusOptions,
        placeholder: 'Status',
    }) as TableColumnFilterProps;

    const levelOfServiceFilterProps = selectFilter({
        options: LevelOfServiceOptions,
        placeholder: 'Type',
    }) as TableColumnFilterProps;

    const engagementBuyersFilterProps = selectFilter({
        options: engagedBuyersOptions,
        placeholder: 'Corporation',
    }) as TableColumnFilterProps;

    const observerFilterProps = selectFilter({
        options: brokersList
            .filter((b) => b.userId)
            .reduce(
                (acc, b) => {
                    acc[b.name] = b.name;
                    return acc;
                },
                {} as Record<string, string>
            ),
        placeholder: 'Observer',
    }) as TableColumnFilterProps;

    const getTooltipBuyersList = (buyers: EngagementBuyer[]) => {
        const content = (
            <div>
                {buyers.map((buyer) => {
                    return <div className="mb-1">{buyer.companyName}</div>;
                })}
            </div>
        );

        return content;
    };

    const getBrokerOnClickActionOrUndefined = (
        brokerId?: number
    ): (() => void) | undefined => {
        if (!brokerId) return undefined;

        const broker = brokersList.find((b) => b.id === brokerId);
        if (!broker?.userId) return undefined;

        return () => setSelectedBroker(broker);
    };

    const getSuspendComponent = (row: Timeline) => {
        return row.engagementProcess.broker ? (
            <>
                <FontAwesomeIcon
                    icon={
                        row.engagementProcess.broker.isActive
                            ? faEye
                            : faEyeSlash
                    }
                    id={'suspend-btn-' + row.id}
                    data-tooltip-html={
                        row.engagementProcess.broker.isActive
                            ? 'Suspend Advisor'
                            : 'Unsuspend Advisor'
                    }
                    onClick={() => setEngagementToUpdateBrokerStatus(row)}
                />

                <Tooltip
                    anchorId={'suspend-btn-' + row.id}
                    place="top"
                    className="tooltip-content place-top mt-m1"
                    classNameArrow="arrow"
                    offset={0}
                />
            </>
        ) : (
            <></>
        );
    };

    const engagementsColumns = [
        {
            dataField: 'id',
            text: 'Id',
            headerClasses: 'id-column-header',
            classes: 'id-column-cell',
            sort: true,
            formatter: (_: any, row: Timeline) => (
                <span className="notification-flag-wrapper">
                    {row.id}
                    <NotificationFlag
                        doNotRemove={true}
                        eventCode={`${NotificationFlagsCategories.MpCode}.${row.id}.${NotificationFlagsCategories.SellerProfileUpdatedCode}`}
                        includePrefix={true}
                    />
                </span>
            ),
        },
        {
            dataField: 'engagementProcess.practices',
            text: 'Practice Name',
            headerClasses: 'practice-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: any[], row: Timeline) => {
                const practiceNames = cellContent?.map((practice) => {
                    return practice.practiceName;
                });

                return cellContent == null || cellContent.length === 0 ? (
                    'NA'
                ) : (
                    <ShowFirstThenFullTextOnHoverLabel items={practiceNames} />
                );
            },
        },
        {
            dataField: 'engagementProcess.userName',
            text: 'Owner Representative',
            headerClasses: 'username-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: string) =>
                !cellContent ? 'NA' : cellContent,
        },
        {
            dataField: 'levelOfService',
            text: 'Type',
            headerClasses: 'type-column-header',
            sort: true,
            filter: levelOfServiceFilterProps,
            formatter: (
                cellContent: LevelOfService,
                row: any,
                index: number
            ) => {
                const value = LevelsOfService.find(
                    (item) => item.id === cellContent
                );
                return value ? (
                    <div className="d-flex justify-content-start" key={index}>
                        <span
                            id={'tab-info-' + index}
                            className="tooltip-target"
                            data-tooltip-html={sanitizeHtml(value.name)}>
                            {value.shortName}{' '}
                        </span>
                        <Tooltip
                            anchorId={'tab-info-' + index}
                            place="top"
                            className="tooltip-content place-top mt-m1"
                            classNameArrow="arrow"
                            offset={0}
                        />
                    </div>
                ) : (
                    'NA'
                );
            },
        },
        {
            dataField: 'engagementProcess.status',
            text: 'Status',
            headerClasses: 'status-column-header',
            sort: true,
            filter: engagementProcessStatusFilterProps,
            formatter: (cellContent: EngagementProcessStatus, row: Timeline) =>
                cellContent === EngagementProcessStatus.Terminated ||
                cellContent === EngagementProcessStatus.Completed ? (
                    EngagementProcessStatuses.find((e) => e.id === cellContent)
                        .name
                ) : (
                    <div className="customerStyle">
                        <select
                            className="selectCustomer"
                            value={cellContent}
                            onChange={(event) => {
                                launchUpdateStatusProcess(
                                    Number(
                                        event.target.value
                                    ) as EngagementProcessStatus,
                                    row
                                );
                            }}>
                            {[
                                ...EngagementProcessStatuses.filter((c) =>
                                    Array.from(
                                        EngagementProcessTransitions.get(
                                            cellContent
                                        )
                                    ).includes(c.id)
                                ),
                            ].map((item) => (
                                <option
                                    value={item.id}
                                    key={'contentLevel ' + item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </div>
                ),
        },
        {
            dataField: 'currentEvent',
            text: 'Current Stage / Event',
            headerClasses: 'stage-event-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: TimelineEvent) => {
                return cellContent
                    ? `${cellContent?.stageName} / ${cellContent?.name}`
                    : 'NA';
            },
        },
        {
            dataField: 'engagementProcess.deadline',
            text: 'Deadline',
            sort: true,
            headerClasses: 'deadline-event-column-header',
            filter: dateFilter({
                placeholder: 'Enter Name',
            }) as TableColumnFilterProps,
            formatter: (cellContent: Date) => (
                <div>
                    {cellContent
                        ? moment(cellContent).format('M/D/YYYY')
                        : 'NA'}
                </div>
            ),
        },
        {
            dataField: 'engagementProcess.broker.brokerId',
            hidden: !isSellers28Implemented,
            text: 'Advisor',
            headerClasses: 'practice-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: number, row: Timeline) => {
                if (
                    !(
                        row.levelOfService ==
                            LevelOfService.FullServiceBrokerage ||
                        row.levelOfService ==
                            LevelOfService.StrategicFinancialAdvisoryService
                    ) ||
                    row.engagementProcess.status ===
                        EngagementProcessStatus.Completed ||
                    row.engagementProcess.status ===
                        EngagementProcessStatus.Terminated
                ) {
                    return <div>N/A</div>;
                } else {
                    return (
                        <EditableNameDropdown
                            isEditMode={isSelectingBroker}
                            selectedValue={cellContent}
                            options={brokersList
                                .filter(
                                    (b) =>
                                        b.userId &&
                                        !row.engagementProcess?.observers?.some(
                                            (o) => o.brokerId === b.id
                                        )
                                )
                                .map((broker) => ({
                                    label: broker.name,
                                    value: broker.id,
                                }))}
                            onItemClick={getBrokerOnClickActionOrUndefined(
                                cellContent
                            )}
                            onChange={(newValue?: string | number) => {
                                setBrokerIdToBeSet(
                                    newValue ? Number(newValue) : 0
                                );
                                setSelectedEngagement(row);
                                return false;
                            }}
                            onEditModeChange={setIsSelectingBroker}
                            unselectedText="None"
                            allowUnselect
                            saveOnChange
                            editModeFormatter={(
                                dropdownComponent,
                                cancelComponent
                            ) => {
                                return (
                                    <>
                                        {dropdownComponent}
                                        {getSuspendComponent(row)}
                                        {cancelComponent}
                                    </>
                                );
                            }}
                        />
                    );
                }
            },
        },
        {
            dataField: 'engagementProcess.observers',
            text: 'Observers',
            headerClasses: 'practice-column-header',
            sort: true,
            filter: observerFilterProps,
            formatter: (observers: EngagementObserver[], row: Timeline) => {
                const list = observers ?? [];
                const renderNames = () => {
                    if (list.length === 0) return 'None';
                    if (list.length === 1) {
                        const broker = brokersList.find(
                            (b) => b.id === list[0].brokerId
                        );
                        return (
                            <div
                                className={
                                    broker?.userId
                                        ? 'editable-name-dropdown-item-view-link'
                                        : undefined
                                }
                                onClick={
                                    broker?.userId
                                        ? () => setSelectedObserver(broker)
                                        : undefined
                                }>
                                {list[0].brokerName}
                            </div>
                        );
                    }
                    return (
                        <ShowFirstThenFullTextOnHoverLabel
                            items={list.map((o) => o.brokerName)}
                            tooltipKey={`observers-${row.id}`}
                        />
                    );
                };
                return (
                    <div className="editable-name-dropdown-wrapper">
                        <div className="editable-name-dropdown-view">
                            {renderNames()}
                            <FontAwesomeIcon
                                icon={faEdit}
                                size="lg"
                                onClick={() =>
                                    setManagingObserversForEngagement(row)
                                }
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            dataField: 'engagementProcess.engagedBuyers',
            text: 'Engaged Buyers',
            sort: true,
            headerClasses: 'engaged-buyers-column-header',
            filter: engagementBuyersFilterProps,
            formatter: (cellContent: EngagementBuyer[], row: Timeline) => {
                const buyersCount = cellContent?.length;

                if (buyersCount) {
                    return (
                        <div className="engaged-buyers-cell">
                            {buyersCount > 1
                                ? `${buyersCount} Buyers`
                                : '1 Buyer'}
                            <InfoTooltip
                                idText={`engaged-buyers-list-${row.id}`}
                                text={''}
                                html={ReactDOMServer.renderToString(
                                    getTooltipBuyersList(cellContent)
                                )}
                            />
                        </div>
                    );
                } else {
                    return <div>{'No Buyers'}</div>;
                }
            },
        },
        {
            dataField: '',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            classes: 'action-column-cell',
            headerFormatter: () => (
                <div className="d-flex w-100">
                    <div className="w-100">Actions</div>
                </div>
            ),
            formatter: (cellContent: any, row: Timeline) => {
                return (
                    <div className="engagement-buttons">
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {
                                navigate(
                                    `/marketplace/admin/engagement/${row.id}`
                                );
                            }}>
                            Seller Dashboard
                        </Button>
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {
                                dispatch(resetCurrentTimeline());
                                navigate(
                                    `/marketplace/admin/engagements/${row.id}`
                                );
                            }}>
                            Timeline
                        </Button>
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {}}>
                            Fee Schedule
                        </Button>
                    </div>
                );
            },
        },
    ];

    const onSuspendStatusUpdateConfirmation = () => {
        setIsSavingSuspendStatus(true);
        axios
            .put(
                `/api/marketplace/MarketplaceBrokers/engagement/${
                    engagementToUpdateBrokerStatus.id
                }/${
                    engagementToUpdateBrokerStatus.engagementProcess.broker
                        .brokerId
                }/suspendedStatus/${!engagementToUpdateBrokerStatus
                    .engagementProcess.broker.isActive}`,
                statusToBeSet,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    createNotification(
                        'Advisor status has been updated successfully',
                        'success'
                    );
                } else {
                    createNotification(
                        'An unexpected error occurred during Advisor status update',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred during Advisor status update',
                    'error'
                );
            })
            .finally(() => {
                fetchEngagements();
                setIsSavingSuspendStatus(false);
                setEngagementToUpdateBrokerStatus(undefined);
            });
    };

    return (
        <div className="engagements">
            <div>
                <div className="mb-3">
                    <strong>Engagements</strong>
                    <Button
                        type="button"
                        className="btn btn-primary ml-3 right-btn"
                        onClick={() => setIsCreateModalVisible(true)}>
                        Create New Engagement
                    </Button>
                </div>
                <div
                    role="tabpanel"
                    id="panel-0"
                    aria-labelledby="tab-0"
                    aria-hidden="false">
                    <div className="card-body">
                        {isLoading ? (
                            Helpers.renderTableLoader()
                        ) : (
                            <BootstrapTable
                                striped
                                bordered
                                bootstrap4
                                remote
                                keyField="id"
                                data={engagements}
                                columns={engagementsColumns}
                                defaultSorted={defaultSorted}
                                filter={filterFactory()}
                                headerClasses="header-vetrical-align-top"
                                pagination={paginationFactory({
                                    page,
                                    sizePerPage,
                                    totalSize,
                                    tooltipTargetId:
                                        'timeline-templates-tooltip',
                                })}
                                onTableChange={handleTableChange}
                                noDataIndication="No Engagements to display"
                                rowStyle={{ height: '44px' }}
                            />
                        )}
                    </div>
                </div>
                {isCreateModalVisible ? (
                    <CreateTimelineModal
                        isVisible={isCreateModalVisible}
                        onClose={() => {
                            setIsCreateModalVisible(false);
                            fetchEngagements();
                        }}
                    />
                ) : (
                    []
                )}
                {(brokerIdToBeSet > 0 || brokerIdToBeSet == -1) &&
                selectedEngagement ? (
                    <ConfirmationModal
                        isVisible={
                            (brokerIdToBeSet > 0 || brokerIdToBeSet == -1) &&
                            selectedEngagement
                        }
                        setIsVisible={() => {
                            setBrokerIdToBeSet(0);
                            setSelectedEngagement(undefined);
                            setIsSelectingBroker(false);
                        }}
                        header="Update Engagement's Advisor"
                        confirmationText={
                            brokerIdToBeSet > 0
                                ? `Are you sure you want to update the Advisor of the engagement to ${
                                      brokersList.find(
                                          (b) => b.id === brokerIdToBeSet
                                      )?.name ?? 'None'
                                  }?`
                                : `Are you sure you want to remove the Advisor from the engagement?`
                        }
                        onConfirm={() => onBrokerUpdateConfirmation()}
                        onCancel={() => {
                            setBrokerIdToBeSet(0);
                            setSelectedEngagement(undefined);
                            setIsSelectingBroker(false);
                        }}
                        params={undefined}
                        nextButtonText={undefined}
                        cancelButtonText={undefined}
                        isLoading={isSavingBroker}
                        closeOnConfirm={false}
                    />
                ) : (
                    []
                )}
                {!!selectedBroker && (
                    <div className="user-details-popup">
                        <UserDetailsModal
                            onClose={() => setSelectedBroker(undefined)}
                            isVisisble={!!selectedBroker}
                            userID={selectedBroker.userId}
                        />
                    </div>
                )}
                {!!selectedObserver && (
                    <div className="user-details-popup">
                        <UserDetailsModal
                            onClose={() => setSelectedObserver(undefined)}
                            isVisisble={!!selectedObserver}
                            userID={selectedObserver.userId}
                        />
                    </div>
                )}
                {statusToBeSet >= 0 && selectedEngagement ? (
                    <ConfirmationModal
                        isVisible={statusToBeSet >= 0}
                        setIsVisible={() => {
                            fetchEngagements();
                            setIsSavingStatus(false);
                            setSelectedEngagement(undefined);
                            setStatusToBeSet(undefined);
                        }}
                        header="Update Engagement's Status"
                        confirmationText={`Are you sure you want to update the status of the engagement to ${
                            EngagementProcessStatuses.find(
                                (s) => s.id === statusToBeSet
                            )?.name
                        }?`}
                        onConfirm={() => onStatusUpdateConfirmation()}
                        params={undefined}
                        nextButtonText={undefined}
                        cancelButtonText={undefined}
                        isLoading={isSavingStatus}
                        closeOnConfirm={false}
                    />
                ) : (
                    []
                )}
                {engagementToUpdateBrokerStatus ? (
                    <ConfirmationModal
                        isVisible={!!engagementToUpdateBrokerStatus}
                        setIsVisible={() => {
                            setEngagementToUpdateBrokerStatus(undefined);
                        }}
                        header={
                            engagementToUpdateBrokerStatus.engagementProcess
                                .broker?.isActive
                                ? "Suspend Advisor's Access"
                                : "Unsuspend Advisor's Access"
                        }
                        confirmationText={
                            engagementToUpdateBrokerStatus.engagementProcess
                                .broker?.isActive
                                ? `Are you sure you want to suspend the access for ${engagementToUpdateBrokerStatus.engagementProcess.broker?.brokerName}?`
                                : `Are you sure you want to unsuspend the access for ${engagementToUpdateBrokerStatus.engagementProcess.broker?.brokerName}?`
                        }
                        onConfirm={() => onSuspendStatusUpdateConfirmation()}
                        params={undefined}
                        nextButtonText={undefined}
                        cancelButtonText={undefined}
                        isLoading={isSavingSuspendStatus}
                        closeOnConfirm={false}
                    />
                ) : (
                    []
                )}
                {managingObserversForEngagement && (
                    <ObserversManagementModal
                        engagementId={managingObserversForEngagement.id}
                        currentBrokerId={
                            managingObserversForEngagement.engagementProcess
                                ?.broker?.brokerId
                        }
                        initialObservers={
                            managingObserversForEngagement.engagementProcess
                                ?.observers ?? []
                        }
                        brokersList={brokersList}
                        onClose={() => setManagingObserversForEngagement(undefined)}
                    />
                )}
            </div>
        </div>
    );
};

export default Engagements;
